import { IonRouterLink } from '@ionic/react';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import PrimaryButton from '@components/buttons/PrimaryButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import BasicCard from '@components/cards/BasicCard';
import styles from '@components/carePlan/CarePlan.module.css';
import Dot from '@components/Dot';
import ExpertAvatar from '@components/ExpertAvatar';
import HoverText from '@components/HoverText';
import Popover from '@components/popovers/Popover';
import TaskPopover from '@components/TaskPopover';
import Icon from '@components/text/Icon';
import MiniCopy from '@components/text/MiniCopy';
import StatusText from '@components/text/StatusText';
import BodyCopy from '@components/typography/BodyCopy';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyClient from '@hooks/useMyClient';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import {
  GetJourneyWithPlanQuery,
  TaskToggleCompletedDocument,
  TaskToggleCompletedMutation,
  TaskToggleCompletedMutationVariables
} from '@typing/Generated';
import { Status } from '@typing/Models';
import { colorForStatus, sortByPosition } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Task = GetJourneyWithPlanQuery['journey']['clientVisibleTasks'][0];

type Props = {
  handleEdit: (task: Task) => void;
  hover?: boolean;
  journey: GetJourneyWithPlanQuery['journey'];
  memberView?: boolean;
  task: Task;
};

const ExpertService = ({ handleEdit, hover = true, journey, memberView = true, task }: Props) => {
  const myClient = useMyClient();
  const myExpert = useMyExpert();
  const intl = useIntl();
  const PopoverStatusOpener = useOpener();
  const resourceOpener = useOpener();
  const isArchived = task.archived || (task.goals.length > 0 && task.goals.every(goal => goal.archived));

  const isMemberView = !!(myClient || memberView);

  const [taskToggleCompleted] = useMutationWithValidation<
    TaskToggleCompletedMutation,
    TaskToggleCompletedMutationVariables
  >(TaskToggleCompletedDocument, 'task');

  const handleCompletedChange = useCallback(
    (completed: boolean) => {
      PopoverStatusOpener.close();
      taskToggleCompleted({
        completed,
        id: task.id
      });
    },
    [PopoverStatusOpener, task.id, taskToggleCompleted]
  );

  const status = useMemo(() => {
    if (!!(task.canceled || !task.completed || task.resources.length == 0)) {
      return Status.IN_PROGRESS;
    } else if (
      task.resources.some(resource => resource.ownerViewedAt === null) &&
      task.resources.some(resource => resource.ownerViewedAt)
    ) {
      return Status.UPDATED;
    }
    return Status.COMPLETED;
  }, [task.canceled, task.completed, task.resources]);

  const popoverButtonContent = (
    <div className={styles.taskPopover}>
      <Dot color={colorForStatus(status)} unread />
      <MiniCopy color="neutral500">
        <FormattedMessage id={`pages.carePlan.base.expertServices.statuses.${status}`} />
      </MiniCopy>
      <Icon ariaLabelKey="accessibility.ariaLabels.open" className={styles.openDropdown} icon="chevronDown" />
    </div>
  );

  return (
    <IonRouterLink
      className={status === Status.IN_PROGRESS || !memberView ? styles.noCursor : ''}
      data-test="expert-service"
      routerLink={
        status === Status.IN_PROGRESS || !hover
          ? undefined
          : journeyRoute({
              journeyId: journey.id,
              linkAfterJourney: `/client-visible-tasks/${task.id}`,
              myClient
            })
      }
    >
      <BasicCard
        hover={status === Status.IN_PROGRESS || !memberView ? 'none' : 'purple'}
        inactive={isArchived}
        padding="small"
      >
        <div className={`${styles.task} ${isMemberView ? styles.center : ''}`}>
          <div>
            <div className={styles.taskContent}>
              <ExpertAvatar expert={task.expert} size={36} />
              <BodyCopy color="neutral700">{task.title}</BodyCopy>
            </div>
            {myExpert && !isMemberView && (
              <div className={styles.taskBottomRow}>
                <div>
                  <HoverText
                    content={
                      task.resources.length === 0
                        ? intl.formatMessage({ id: 'pages.carePlan.base.expertServices.mustHaveResources' })
                        : undefined
                    }
                  >
                    <div className={styles.smallFlex + ' ' + styles.noWrap}>
                      <Popover
                        buttonContent={popoverButtonContent}
                        color="primary700"
                        disabled={task.resources.length === 0}
                        isOpen={PopoverStatusOpener.isOpen}
                        opener={PopoverStatusOpener}
                      >
                        <IonRouterLink onClick={() => handleCompletedChange(true)}>
                          <div className={styles.popoverItem}>
                            <Dot color={colorForStatus(Status.COMPLETED)} unread />
                            <MiniCopy color="neutral500">
                              <FormattedMessage
                                id={`pages.carePlan.base.expertServices.statuses.${Status.COMPLETED}`}
                              />
                            </MiniCopy>
                          </div>
                        </IonRouterLink>
                        <IonRouterLink onClick={() => handleCompletedChange(false)}>
                          <div className={styles.popoverItem}>
                            <Dot color={colorForStatus(Status.IN_PROGRESS)} unread />
                            <MiniCopy color="neutral500">
                              <FormattedMessage
                                id={`pages.carePlan.base.expertServices.statuses.${Status.IN_PROGRESS}`}
                              />
                            </MiniCopy>
                          </div>
                        </IonRouterLink>
                      </Popover>
                    </div>
                  </HoverText>
                </div>
                <div className={styles.buttonAndToggle}>
                  {task.resources.length > 0 ? (
                    <PrimaryButton
                      routerLink={journeyRoute({
                        journeyId: journey.id,
                        linkAfterJourney: `/client-visible-tasks/${task.id}`,
                        myExpert
                      })}
                      textKey="pages.carePlan.base.expertServices.editResources"
                    />
                  ) : (
                    <TertiaryButton
                      icon="add"
                      routerLink={journeyRoute({
                        journeyId: journey.id,
                        linkAfterJourney: `/client-visible-tasks/${task.id}`,
                        myExpert
                      })}
                      textKey="pages.carePlan.base.expertServices.addResources"
                    />
                  )}
                  {task.resources.length > 0 && (
                    <div
                      className={
                        task.resources.some(r => r.draft) ? styles.draftResourcesToggle : styles.resourcesToggle
                      }
                      onClick={resourceOpener.toggle}
                    >
                      {task.resources.some(r => r.draft) && <StatusText iconOnly status={Status.DRAFT} />}
                      <Icon
                        ariaLabelKey="accessibility.ariaLabels.open"
                        className={styles.resourceToggle}
                        icon={resourceOpener.isOpen ? 'caretUp' : 'caretDown'}
                      />
                    </div>
                  )}
                </div>
                <div></div>
              </div>
            )}
          </div>

          {isMemberView && (
            <div>
              <div className={styles.noWrap}>
                <StatusText status={status} />
              </div>
            </div>
          )}

          {myExpert && !isMemberView && (
            <div className={styles.notPublishedAndEdit}>
              <div className={styles.controls}>
                <TaskPopover handleEdit={handleEdit} journey={journey} task={task} />
                {task.draft ? (
                  <StatusText iconOnly status={Status.DRAFT} />
                ) : task.resources.every(resource => resource.ownerViewedAt) && task.resources.length > 0 ? (
                  <StatusText iconOnly status={Status.VIEWED} />
                ) : null}
              </div>
            </div>
          )}
        </div>
        <div>
          <ExpandableSection isOpen={resourceOpener.isOpen}>
            <div className={styles.taskResources}>
              {sortByPosition(task.resources).map(resource => (
                <div className={styles.taskResource} key={resource.id}>
                  <BodyCopy color="neutral700">{resource.title}</BodyCopy>

                  {resource.draft ? (
                    <StatusText iconOnly status={Status.DRAFT} />
                  ) : resource.ownerViewedAt ? (
                    <StatusText iconOnly status={Status.VIEWED} />
                  ) : null}
                </div>
              ))}
            </div>
          </ExpandableSection>
        </div>
      </BasicCard>
    </IonRouterLink>
  );
};

export default ExpertService;
